import { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { firebaseUserAtom } from '@/stores/auth';
import { DailyMIT } from '@/types';
import { queryKeys } from '@/lib/queryKeys';
import * as mitsRepo from '@/repositories/daily-mits.repository';
import { getCompletedMitsCount, canAddMit, sortMitsByOrder } from '@/services/daily-mits.service';

// ============================================
// Types
// ============================================

export interface AddMITInput {
  title: string;
  taskId?: string;
  blockId?: string;
  order: number;
}

export interface UpdateMITInput {
  taskId?: string;
  blockId?: string;
  title?: string;
}

export interface UseDailyMITsResult {
  mits: DailyMIT[];
  completedCount: number;
  isLoading: boolean;
  addMIT: (data: AddMITInput) => Promise<boolean>;
  updateMIT: (mitId: string, data: UpdateMITInput) => Promise<boolean>;
  completeMIT: (mitId: string) => Promise<boolean>;
  removeMIT: (mitId: string) => Promise<boolean>;
  reorderMITs: (mits: DailyMIT[]) => Promise<boolean>;
}

// ============================================
// Hook
// ============================================

export function useDailyMITs(date: string): UseDailyMITsResult {
  const firebaseUser = useAtomValue(firebaseUserAtom);
  const queryClient = useQueryClient();

  const [isListenerInitialized, setIsListenerInitialized] = useState(false);
  const prevKeyRef = useRef<string | null>(null);

  const userId = firebaseUser?.uid;
  const queryKey = queryKeys.dailyMITs.byDate(userId, date);
  const cacheKey = `${userId}-${date}`;

  // Use React Query for caching and state management
  const { data: mits = [] } = useQuery<DailyMIT[], Error>({
    queryKey,
    queryFn: async () => [],
    enabled: !!userId,
    staleTime: Infinity,
  });

  // Set up real-time Firestore listener via repository
  useEffect(() => {
    if (prevKeyRef.current !== cacheKey) {
      prevKeyRef.current = cacheKey;
      setIsListenerInitialized(false);
    }

    if (!userId) {
      setIsListenerInitialized(true);
      return;
    }

    let isCleanedUp = false;

    const timeoutId = setTimeout(() => {
      if (!isCleanedUp && !isListenerInitialized) {
        console.warn('Daily MITs listener timeout - marking as initialized');
        setIsListenerInitialized(true);
      }
    }, 2000);

    const unsubscribe = mitsRepo.subscribeToMITs(
      userId,
      date,
      (sortedMits) => {
        if (isCleanedUp) return;
        queryClient.setQueryData(queryKey, sortedMits);
        setIsListenerInitialized(true);
      },
      () => {
        if (!isCleanedUp) {
          setIsListenerInitialized(true);
        }
      }
    );

    return () => {
      isCleanedUp = true;
      clearTimeout(timeoutId);
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  // Compute completed count via service
  const completedCount = useMemo(() => getCompletedMitsCount(mits), [mits]);

  // Add a new MIT (max 3)
  const addMIT = useCallback(
    async (data: AddMITInput): Promise<boolean> => {
      if (!userId) return false;

      const currentMits = queryClient.getQueryData<DailyMIT[]>(queryKey) || [];
      if (!canAddMit(currentMits.length)) return false;

      // Build MIT input (omit undefined fields for Firestore)
      const mitInput: Omit<DailyMIT, 'id'> = {
        title: data.title.trim(),
        completed: false,
        order: data.order,
        ...(data.taskId !== undefined && { taskId: data.taskId }),
        ...(data.blockId !== undefined && { blockId: data.blockId }),
      };

      // Optimistic update
      const optimisticMIT: DailyMIT = { id: mitsRepo.generateMITId(), ...mitInput };
      const updatedMits = sortMitsByOrder([...currentMits, optimisticMIT]);
      queryClient.setQueryData(queryKey, updatedMits);

      try {
        await mitsRepo.addMIT(userId, date, mitInput);
        return true;
      } catch (error) {
        console.error('Error adding MIT:', error);
        queryClient.setQueryData(queryKey, currentMits);
        return false;
      }
    },
    [userId, queryClient, queryKey, date]
  );

  // Update an existing MIT
  const updateMIT = useCallback(
    async (mitId: string, data: UpdateMITInput): Promise<boolean> => {
      if (!userId) return false;

      const currentMits = queryClient.getQueryData<DailyMIT[]>(queryKey) || [];
      const mitIndex = currentMits.findIndex((m) => m.id === mitId);
      if (mitIndex === -1) return false;

      // Build update object
      const updates: Partial<DailyMIT> = {};
      if (data.taskId !== undefined) updates.taskId = data.taskId;
      if (data.blockId !== undefined) updates.blockId = data.blockId;
      if (data.title !== undefined) updates.title = data.title;

      // Optimistic update
      const updatedMits = [...currentMits];
      updatedMits[mitIndex] = { ...updatedMits[mitIndex], ...updates };
      queryClient.setQueryData(queryKey, updatedMits);

      try {
        await mitsRepo.updateMIT(userId, date, mitId, updates);
        return true;
      } catch (error) {
        console.error('Error updating MIT:', error);
        queryClient.setQueryData(queryKey, currentMits);
        return false;
      }
    },
    [userId, queryClient, queryKey, date]
  );

  // Toggle MIT completion status
  const completeMIT = useCallback(
    async (mitId: string): Promise<boolean> => {
      if (!userId) return false;

      const currentMits = queryClient.getQueryData<DailyMIT[]>(queryKey) || [];
      const mitIndex = currentMits.findIndex((m) => m.id === mitId);
      if (mitIndex === -1) return false;

      // Optimistic update
      const updatedMits = [...currentMits];
      updatedMits[mitIndex] = {
        ...updatedMits[mitIndex],
        completed: !updatedMits[mitIndex].completed,
      };
      queryClient.setQueryData(queryKey, updatedMits);

      try {
        await mitsRepo.toggleMITCompletion(userId, date, mitId);
        return true;
      } catch (error) {
        console.error('Error toggling MIT completion:', error);
        queryClient.setQueryData(queryKey, currentMits);
        return false;
      }
    },
    [userId, queryClient, queryKey, date]
  );

  // Remove an MIT
  const removeMIT = useCallback(
    async (mitId: string): Promise<boolean> => {
      if (!userId) return false;

      const currentMits = queryClient.getQueryData<DailyMIT[]>(queryKey) || [];
      const updatedMits = currentMits.filter((m) => m.id !== mitId);
      if (updatedMits.length === currentMits.length) return false;

      // Optimistic update
      queryClient.setQueryData(queryKey, updatedMits);

      try {
        await mitsRepo.removeMIT(userId, date, mitId);
        return true;
      } catch (error) {
        console.error('Error removing MIT:', error);
        queryClient.setQueryData(queryKey, currentMits);
        return false;
      }
    },
    [userId, queryClient, queryKey, date]
  );

  // Reorder MITs
  const reorderMITs = useCallback(
    async (newMits: DailyMIT[]): Promise<boolean> => {
      if (!userId) return false;

      const currentMits = queryClient.getQueryData<DailyMIT[]>(queryKey) || [];
      const sortedMits = sortMitsByOrder(newMits);

      // Optimistic update
      queryClient.setQueryData(queryKey, sortedMits);

      try {
        await mitsRepo.updateMITs(userId, date, sortedMits);
        return true;
      } catch (error) {
        console.error('Error reordering MITs:', error);
        queryClient.setQueryData(queryKey, currentMits);
        return false;
      }
    },
    [userId, queryClient, queryKey, date]
  );

  // Compute actual loading state
  const hasMits = mits.length > 0;
  const isLoading = !!userId && !isListenerInitialized && !hasMits;

  return {
    mits,
    completedCount,
    isLoading,
    addMIT,
    updateMIT,
    completeMIT,
    removeMIT,
    reorderMITs,
  };
}
